import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAcknowledgePolicyMutation, useMyDocumentsQuery, useMyEquipmentQuery, useMyPoliciesQuery, useOpenSigned } from '@/client/api/hr/records';
import { HR_DOCUMENT_KIND_LABEL, HR_POLICY_CATEGORY_LABEL, type HrPolicy } from '@/client/types/hr';
import { EmptyState, Panel, StatusBadge } from '../HumanResource/components/hr-ui';
import { formatDay } from '../HumanResource/components/leave-format';

/** Policies to read and acknowledge, and the files HR has shared with me. */
export function DocumentsTab() {
  const policiesQ = useMyPoliciesQuery();
  const docsQ = useMyDocumentsQuery();
  const ack = useAcknowledgePolicyMutation();
  const open = useOpenSigned('workplace');
  const [reading, setReading] = useState<HrPolicy | null>(null);

  if (policiesQ.isLoading || docsQ.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white py-14 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your documents…
      </div>
    );
  }
  if (policiesQ.isError) return <EmptyState label={`Could not load policies: ${(policiesQ.error as Error)?.message ?? ''}`} />;
  const policies = policiesQ.data ?? [];
  const toAck = policies.filter((p) => p.requiresAck && !p.acknowledgedByMe);
  const docs = docsQ.data ?? [];

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title={toAck.length ? `Policies (${toAck.length} to acknowledge)` : 'Policies'}>
        {policies.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">No policies published yet.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {policies.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2.5">
                <button type="button" className="min-w-0 text-left" onClick={() => setReading(p)}>
                  <p className="truncate text-sm text-gray-900 hover:text-blue-700">{p.title}</p>
                  <p className="text-xs text-gray-500">
                    {HR_POLICY_CATEGORY_LABEL[p.category]} · v{p.version}
                  </p>
                </button>
                {p.requiresAck ? (
                  p.acknowledgedByMe ? (
                    <StatusBadge label="Acknowledged" className="bg-emerald-50 text-emerald-700" />
                  ) : (
                    <Button size="sm" onClick={() => setReading(p)}>
                      Read and acknowledge
                    </Button>
                  )
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="My files">
        {docs.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">HR has not shared any files with you.</p>
        ) : (
          <ul className="divide-y divide-gray-50">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 py-2">
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-2 text-left text-sm text-gray-800 hover:text-blue-700"
                  onClick={() => open(`/documents/${d.id}/download`).catch((e) => toast.error((e as Error).message))}
                >
                  <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="truncate">{d.name}</span>
                </button>
                <span className="shrink-0 text-xs text-gray-400">
                  {HR_DOCUMENT_KIND_LABEL[d.kind]}
                  {d.expiresOn && ` · expires ${formatDay(d.expiresOn)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {reading?.title} <span className="text-sm font-normal text-gray-400">v{reading?.version}</span>
            </DialogTitle>
          </DialogHeader>
          <p className="whitespace-pre-wrap text-sm text-gray-800">{reading?.body}</p>
          {reading?.requiresAck && (
            <DialogFooter>
              {reading.acknowledgedByMe ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> You acknowledged this version
                </span>
              ) : (
                <Button
                  disabled={ack.isPending}
                  onClick={() =>
                    ack.mutate(reading.id, {
                      onSuccess: () => {
                        toast.success('Acknowledged');
                        setReading(null);
                      },
                      onError: (e) => toast.error((e as Error).message),
                    })
                  }
                >
                  I have read and understood this policy
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Company equipment I have. */
export function EquipmentPanel() {
  const q = useMyEquipmentQuery();
  const items = q.data ?? [];
  return (
    <Panel title="My equipment">
      {q.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-500">No company equipment is assigned to you.</p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {items.map((a) => (
            <li key={a.id} className="py-2 text-sm">
              <p className="text-gray-900">{a.name}</p>
              <p className="text-xs text-gray-500">
                {a.tag}
                {a.serial && ` · ${a.serial}`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
